import { Injectable } from '@angular/core';
import { HubConnection } from '@aspnet/signalr';
import * as signalR from '@aspnet/signalr';
import { ModuleConfig } from '../configs/module-config';
import { ModuleConfigService } from './module-config.service';
import { VsAuthenService } from '../auth/authen.service';

@Injectable()
export class SignalRService {
    private _moduleConfig: ModuleConfig;
    private _hubConnection: HubConnection;
    private _currentHub: string;
    private _isStarting = false;
    private _manualStop = false;
    private _reconnectTimer: any;

    maxRetryTimes = 10;
    viewCodeSubs: {
        viewCode: string,
        callback: (data: any) => void
    }[] = [];

    constructor(
        private _moduleConfigService: ModuleConfigService,
        private _authenService: VsAuthenService
    ) {
        this._moduleConfig = this._moduleConfigService.getConfig();
        this.registerBrowserReconnectEvents();
    }

    start(hub: string, viewCode: string, callback: () => void) {
        this.subscribeViewCode(viewCode, callback);
        this.ensureConnection(hub).catch(err => console.error('SignalR start error:', err));
    }

    startList(hub: string, viewCode: string[], callback: () => void) {
        viewCode.forEach(element => this.subscribeViewCode(element, callback));
        this.ensureConnection(hub).catch(err => console.error('SignalR start error:', err));
    }

    stop() {
        this._manualStop = true;
        this.clearReconnectTimer();

        if (this._hubConnection) {
            this._hubConnection.stop().catch(() => { });
        }
    }

    subscribeViewCode(viewCode: string, callback: (data: any) => void) {
        const existed = this.viewCodeSubs.some(item => item.viewCode === viewCode && item.callback === callback);
        if (!existed) {
            this.viewCodeSubs.push({ viewCode, callback });
        }

        if (this.isConnected()) {
            this.subscribe(viewCode, callback);
        }
    }

    private createConnection(hub: string) {
        this._currentHub = hub;
        this._hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${this._moduleConfig.Services.NotificationEndpoint}/${hub}`, {
                accessTokenFactory: () => this._authenService.getAccessToken(),
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this._hubConnection.onclose((err) => {
            this._isStarting = false;
            if (this._manualStop) {
                return;
            }

            console.warn('SignalR closed');
            this.scheduleReconnect();
        });
    }

    private wait = ms => new Promise(r => setTimeout(r, ms));

    private ensureConnection(hub: string): Promise<any> {
        this._manualStop = false;

        if (!this._hubConnection || this._currentHub !== hub) {
            this.createConnection(hub);
        }

        return this.startConnection().then((started) => {
            if (started) {
                this.rejoinAllSubscriptions();
            }
            return true;
        });
    }

    private startConnection(retryCount = 1): Promise<any> {
        if (!this._hubConnection) {
            return Promise.reject('Hub connection has not been created.');
        }

        if (this.isConnected()) {
            return Promise.resolve(true);
        }

        if (this._isStarting) {
            return Promise.resolve(false);
        }

        this._isStarting = true;

        // Keep the legacy workaround already used by this project.
        Object.defineProperty(WebSocket, 'OPEN', { value: 1 });

        return this._hubConnection.start().then(() => {
            this._isStarting = false;
            this.clearReconnectTimer();
            return true;
        }).catch((err) => {
            this._isStarting = false;

            if (this.maxRetryTimes - retryCount > 0) {
                return this.wait(retryCount * retryCount * 1000)
                    .then(() => this.startConnection(retryCount + 1));
            }

            this.scheduleReconnect();
            return Promise.reject(err);
        });
    }

    private subscribe(viewCode: string, callback: (data: any) => void) {
        this._hubConnection.off(viewCode);
        this._hubConnection.invoke('Join', viewCode).catch(err => console.error(err.toString()));
        this._hubConnection.on(viewCode, (data: any) => {
            callback(data);
        });
    }

    private rejoinAllSubscriptions() {
        this.viewCodeSubs.forEach(item => this.subscribe(item.viewCode, item.callback));
    }

    private scheduleReconnect() {
        if (this._manualStop || !this._currentHub || this._reconnectTimer) {
            return;
        }

        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            this.createConnection(this._currentHub);
            this.ensureConnection(this._currentHub).catch(err => {
                console.warn('SignalR reconnect error:', err);
                this.scheduleReconnect();
            });
        }, 3000);
    }

    private clearReconnectTimer() {
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    }

    private isConnected() {
        return this._hubConnection
            && this._hubConnection.state === signalR.HubConnectionState.Connected;
    }

    private registerBrowserReconnectEvents() {
        if (typeof window === 'undefined') {
            return;
        }

        window.addEventListener('online', () => {
            if (this._currentHub && !this.isConnected()) {
                this.ensureConnection(this._currentHub).catch(err => console.error('SignalR online reconnect error:', err));
            }
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && this._currentHub && !this.isConnected()) {
                this.ensureConnection(this._currentHub).catch(err => console.error('SignalR visibility reconnect error:', err));
            }
        });
    }

    // CUSTOM SEND
    private createConnectionDapFoodAPI(hub: string) {
        this._currentHub = hub;
        this._hubConnection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Debug)
            .withUrl('https://devnotification.dapfood.net/NotificationHub', {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets
            })
            .build();

        this._hubConnection.onclose((err) => {
            this._isStarting = false;
            if (this._manualStop) {
                return;
            }

            console.warn('SignalR closed');
            this.scheduleReconnect();
        });
    }

    SendDataWeb(hub: string, Method: string[], data: any) {
        this._manualStop = false;
        this.createConnectionDapFoodAPI(hub);
        this.startConnection().then(() => {
            Method.forEach(element => {
                this.sendDataByViewCode(element, data);
            });
        }).catch(err => console.error('SignalR send error:', err));
    }

    private sendDataByViewCode(Method: string, data: any) {
        this._hubConnection.invoke(Method, data).catch(err => console.error(err.toString()));
    }
}


